import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { ApiError } from '../../api/client';
import { getPermissions, ME_QUERY_KEY, PERMISSIONS_QUERY_KEY, savePermissions } from '../../api/endpoints/permissions';
import { createIdempotencyKeyHolder } from '../../api/idempotency';
import type {
  AccessProfile,
  BusinessAccessRecord,
  BusinessPermissionsResponse,
  PermissionKey,
} from '../../api/schemas';
import { ErrorBanner } from '../../components/ErrorBanner';
import { LoadingState } from '../../components/LoadingState';
import type { PortalMode } from '../../lib/mode';
import {
  ALL_PERMISSIONS,
  COACH_AGENDA_HINT,
  PERMISSION_GROUPS,
  PROFILE_LABEL,
  profilesForMode,
  withPermission,
} from './catalog';
import { Switch } from './Switch';

type Props = {
  /** The accesses on file, to say who each profile reaches. */
  accesses: readonly BusinessAccessRecord[] | undefined;
  /** What kind of business this is: a waiter is only offered to a restaurant. */
  mode?: PortalMode;
};

type Toggle = { profile: AccessProfile; next: PermissionKey[] };

/**
 * "Qué ve cada perfil": the owner picks a profile and switches each door on or
 * off. Every switch saves on its own; the API applies it on that person's next
 * request, so there is nothing to publish.
 */
export function PermisosSection({ accesses, mode }: Props) {
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<AccessProfile>('front_desk');
  const query = useQuery({ queryKey: PERMISSIONS_QUERY_KEY, queryFn: getPermissions });
  // One key per switch press; a retried press after a dropped response replays the same write.
  const saveKey = useRef(createIdempotencyKeyHolder()).current;

  const mutation = useMutation({
    mutationFn: ({ profile: target, next }: Toggle) => savePermissions(target, next, saveKey.ensure()),
    onMutate: async ({ profile: target, next }: Toggle) => {
      await queryClient.cancelQueries({ queryKey: PERMISSIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<BusinessPermissionsResponse>(PERMISSIONS_QUERY_KEY);
      if (previous) queryClient.setQueryData(PERMISSIONS_QUERY_KEY, withProfile(previous, target, next));
      return { previous };
    },
    onError: (_error, _toggle, context) => {
      if (context?.previous) queryClient.setQueryData(PERMISSIONS_QUERY_KEY, context.previous);
    },
    onSuccess: (saved) => queryClient.setQueryData(PERMISSIONS_QUERY_KEY, saved),
    onSettled: () => {
      saveKey.reset();
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });

  const current = query.data?.data.profiles[profile] ?? [];
  const people = peopleWith(accesses, profile);

  return (
    <section className="section permisos" aria-labelledby="permisos-title">
      <h2 id="permisos-title" className="section-label">
        Qué ve cada perfil
      </h2>
      {query.isLoading ? <LoadingState label="Cargando permisos…" /> : null}
      {query.isError ? <ErrorBanner message={errorMessage(query.error)} /> : null}
      {query.data ? (
        <>
          <div className="seg permisos-profiles" role="group" aria-label="Perfil">
            {profilesForMode(mode, profile).map((candidate) => (
              <button
                key={candidate}
                type="button"
                aria-pressed={candidate === profile}
                onClick={() => setProfile(candidate)}
              >
                {PROFILE_LABEL[candidate]}
                <small>{countLabel(query.data.data.profiles[candidate])}</small>
              </button>
            ))}
          </div>
          <p className="permisos-people">
            {people.length > 0
              ? `${PROFILE_LABEL[profile]}: ${people.join(', ')}.`
              : `Nadie tiene el perfil ${PROFILE_LABEL[profile]} todavía. Se lo das a una persona en Accesos.`}
          </p>
          {PERMISSION_GROUPS.map((group) => (
            <div key={group.title} className="permisos-group">
              <h3 className="permisos-group-title">{group.title}</h3>
              <ul className="group rows">
                {group.items.map((item) => {
                  const titleId = `permiso-${item.key}`;
                  const hintId = `permiso-${item.key}-hint`;
                  const hint = profile === 'coach' && item.key === 'agenda' ? COACH_AGENDA_HINT : item.hint;
                  return (
                    <li key={item.key} className="row permisos-row">
                      <div className="grow">
                        <p className="row-title" id={titleId}>
                          {item.label}
                        </p>
                        <p className="row-sub" id={hintId}>
                          {hint}
                        </p>
                      </div>
                      <Switch
                        checked={current.includes(item.key)}
                        labelledBy={titleId}
                        describedBy={hintId}
                        onChange={(allowed) =>
                          mutation.mutate({ profile, next: withPermission(current, item.key, allowed) })
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {mutation.isError ? <ErrorBanner message={errorMessage(mutation.error)} /> : null}
          <p className="permisos-foot" role="status" aria-live="polite">
            {mutation.isPending
              ? 'Guardando…'
              : 'Cada cambio aplica de una vez. Tú siempre ves todo; los accesos, estos permisos y el plan de Dale solo los manejas tú.'}
          </p>
        </>
      ) : null}
    </section>
  );
}

function withProfile(
  response: BusinessPermissionsResponse,
  profile: AccessProfile,
  next: PermissionKey[],
): BusinessPermissionsResponse {
  return { data: { ...response.data, profiles: { ...response.data.profiles, [profile]: next } } };
}

function peopleWith(accesses: readonly BusinessAccessRecord[] | undefined, profile: AccessProfile): string[] {
  return (accesses ?? [])
    .filter((access) => access.role === 'employee' && access.isActive && access.accessProfile === profile)
    .map((access) => access.name);
}

function countLabel(list: readonly PermissionKey[]): string {
  return `${list.length} de ${ALL_PERMISSIONS.length}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos guardar el permiso. Revisa tu conexión y vuelve a intentarlo.';
}
